import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTheme } from '@/contexts/ThemeContext';
import { 
  QrCode, 
  ArrowRight,
  CheckCircle2,
  Clock,
  TrendingUp,
  Users,
  Smartphone,
  ChefHat,
  CreditCard,
  BarChart3,
  Zap,
  Shield,
  Star,
  Play,
  Building2,
  UtensilsCrossed,
  Umbrella,
  Sun,
  Moon,
  Sparkles,
  Timer,
  Receipt,
  MousePointer
} from 'lucide-react';

export default function Landing() {
  const { theme, toggleTheme } = useTheme();
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const stats = [
    { value: '60%', label: 'menos espera', color: 'text-emerald-500' },
    { value: '35%', label: 'más rotación', color: 'text-blue-500' },
    { value: '0', label: 'errores', color: 'text-purple-500' },
    { value: '45%', label: 'más satisfacción', color: 'text-orange-500' },
  ];

  const howItWorks = [
    { step: 1, icon: QrCode, title: 'Escanea', desc: 'Cliente escanea el QR de su mesa' },
    { step: 2, icon: Smartphone, title: 'Pide', desc: 'Ve el menú y arma su pedido' },
    { step: 3, icon: ChefHat, title: 'Prepara', desc: 'Cocina recibe y prepara' },
    { step: 4, icon: Receipt, title: 'Paga', desc: 'Pide la cuenta desde su celular' },
  ];

  const features = [
    {
      icon: QrCode,
      title: 'QR único por mesa',
      description: 'Cada unidad tiene su código. El cliente escanea y empieza a pedir.',
      gradient: 'from-violet-500 to-purple-600'
    },
    {
      icon: Smartphone,
      title: 'Menú siempre actualizado',
      description: 'Fotos, precios, disponibilidad. Cambios al instante sin reimprimir.',
      gradient: 'from-blue-500 to-cyan-600'
    },
    {
      icon: ChefHat,
      title: 'Cocina en tiempo real',
      description: 'Pedidos directo a pantalla. Sin papelitos ni confusiones.',
      gradient: 'from-orange-500 to-red-600'
    },
    {
      icon: CreditCard,
      title: 'Cobro inteligente',
      description: 'Divide cuentas, sugiere propinas, registra pagos. Todo simple.',
      gradient: 'from-emerald-500 to-teal-600'
    },
    {
      icon: BarChart3,
      title: 'Datos que importan',
      description: 'Ventas, productos estrella, horarios pico. Exporta a CSV.',
      gradient: 'from-pink-500 to-rose-600'
    },
    {
      icon: Users,
      title: 'Roles definidos',
      description: 'Mozo, cajero, cocina. Cada uno ve lo que necesita.',
      gradient: 'from-indigo-500 to-violet-600'
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white overflow-hidden">
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-1/2 -right-48 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl animate-pulse delay-500" />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform">
              <QrCode className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight">QR Mesa</span>
          </Link>

          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleTheme}
              className="text-white/70 hover:text-white"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Link to="/login">
              <Button variant="ghost" className="text-white/70 hover:text-white hidden sm:flex">
                Iniciar Sesión
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white shadow-lg shadow-purple-500/25">
                Empezar Gratis
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="mb-6 bg-white/10 text-white/80 border-white/10 px-4 py-1.5 text-sm">
              <Sparkles className="w-4 h-4 mr-2" />
              +500 negocios ya lo usan
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Tu negocio factura{' '}
              <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                más
              </span>
              <br />
              con{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                menos esfuerzo
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/60 mb-10 max-w-2xl mx-auto leading-relaxed">
              Sistema de pedidos por QR para restaurantes, bares y paradores. 
              Tus clientes piden desde su celular, la cocina prepara, y vos cobrás más rápido.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Link to="/register">
                <Button size="lg" className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white text-lg px-8 h-14 shadow-xl shadow-purple-500/30 w-full sm:w-auto">
                  Empezar ahora
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="#demo">
                <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 text-lg px-8 h-14 w-full sm:w-auto">
                  <Play className="mr-2 w-5 h-5" />
                  Ver demo
                </Button>
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
              {stats.map((stat, i) => (
                <div key={i} className="p-4 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors">
                  <p className={`text-3xl md:text-4xl font-bold ${stat.color}`}>{stat.value}</p>
                  <p className="text-white/50 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-4 md:px-8 relative">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
              Simple y Rápido
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold">
              Así de fácil funciona
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {howItWorks.map((item, i) => (
              <div 
                key={i}
                className={`relative p-6 rounded-2xl transition-all duration-500 ${
                  activeFeature === i 
                    ? 'bg-gradient-to-b from-violet-500/20 to-purple-500/10 border-violet-500/50 scale-105' 
                    : 'bg-white/5 border-white/10'
                } border`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  activeFeature === i ? 'bg-violet-500' : 'bg-white/10'
                }`}>
                  <item.icon className="w-6 h-6" />
                </div>
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-slate-900 border-2 border-violet-500 flex items-center justify-center text-sm font-bold">
                  {item.step}
                </div>
                <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-white/50 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 md:px-8 bg-gradient-to-b from-transparent via-white/5 to-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-500/20 text-blue-400 border-blue-500/30">
              Todo incluido
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Funcionalidades que importan
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              No necesitás contratar 10 sistemas distintos. QR Mesa tiene todo lo que necesitás.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <Card 
                key={i} 
                className="bg-white/5 border-white/10 hover:border-white/20 transition-all hover:-translate-y-1 group overflow-hidden"
              >
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-lg text-white mb-2">{feature.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Business Types */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/30">
              Para tu tipo de negocio
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold">
              Adaptado a vos
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="group relative overflow-hidden rounded-3xl">
              <img 
                src="https://images.pexels.com/photos/67468/pexels-photo-67468.jpeg?auto=compress&cs=tinysrgb&w=800" 
                alt="Restaurant"
                className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                    <UtensilsCrossed className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-2xl">Restaurantes</h3>
                </div>
                <p className="text-white/70">
                  Salones, terrazas, patios. Mesas circulares o rectangulares. 
                  Diseña tu espacio como quieras.
                </p>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-3xl">
              <img 
                src="https://images.pexels.com/photos/1174732/pexels-photo-1174732.jpeg?auto=compress&cs=tinysrgb&w=800" 
                alt="Beach Club"
                className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                    <Umbrella className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-2xl">Paradores de Playa</h3>
                </div>
                <p className="text-white/70">
                  Sombrillas, carpas VIP, reposeras. El cliente pide desde la arena 
                  y el mozo le lleva.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" className="py-20 px-4 md:px-8 bg-gradient-to-b from-transparent via-violet-500/10 to-transparent">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-violet-500/20 text-violet-400 border-violet-500/30">
              <MousePointer className="w-4 h-4 mr-2" />
              Probalo ahora
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Cuentas demo listas
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Entrá con cualquiera de estos usuarios y explorá todo el sistema. 
              Probá el menú, hacé pedidos, mirá el panel de cocina.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Restaurant Demo */}
            <Card className="bg-white/5 border-white/10 overflow-hidden group hover:border-violet-500/50 transition-colors">
              <div className="h-2 bg-gradient-to-r from-orange-500 to-red-500" />
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                    <UtensilsCrossed className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-white">Restaurant La Costanera</h3>
                    <p className="text-white/50 text-sm">Cocina argentina tradicional</p>
                  </div>
                </div>
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-purple-500/20 text-purple-400 text-xs">Admin</Badge>
                      <span className="text-white/70">demo.costanera@qrmesa.com</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-orange-500/20 text-orange-400 text-xs">Cocina</Badge>
                      <span className="text-white/70">cocina.costanera@qrmesa.com</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">Caja</Badge>
                      <span className="text-white/70">caja.costanera@qrmesa.com</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-500/20 text-blue-400 text-xs">Mozo</Badge>
                      <span className="text-white/70">mozo.costanera@qrmesa.com</span>
                    </div>
                  </div>
                </div>
                
                <p className="text-xs text-white/40 mt-4 text-center">
                  Contraseña: <code className="bg-white/10 px-2 py-0.5 rounded">demo123</code>
                </p>
              </CardContent>
            </Card>

            {/* Beach Demo */}
            <Card className="bg-white/5 border-white/10 overflow-hidden group hover:border-cyan-500/50 transition-colors">
              <div className="h-2 bg-gradient-to-r from-cyan-500 to-blue-500" />
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                    <Umbrella className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-white">Parador Sol & Mar</h3>
                    <p className="text-white/50 text-sm">Playa Grande, Mar del Plata</p>
                  </div>
                </div>
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-purple-500/20 text-purple-400 text-xs">Admin</Badge>
                      <span className="text-white/70">demo.solymar@qrmesa.com</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-orange-500/20 text-orange-400 text-xs">Cocina</Badge>
                      <span className="text-white/70">cocina.solymar@qrmesa.com</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">Caja</Badge>
                      <span className="text-white/70">caja.solymar@qrmesa.com</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-500/20 text-blue-400 text-xs">Mozo</Badge>
                      <span className="text-white/70">mozo.solymar@qrmesa.com</span>
                    </div>
                  </div>
                </div>
                
                <p className="text-xs text-white/40 mt-4 text-center">
                  Contraseña: <code className="bg-white/10 px-2 py-0.5 rounded">demo123</code>
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-10 text-center">
            <Link to="/login">
              <Button size="lg" className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-lg px-10 h-14 shadow-xl shadow-purple-500/30">
                Probar Demo Ahora
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
              <Star className="w-4 h-4 mr-2 fill-current" />
              Lo que dicen nuestros clientes
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold">
              Historias de éxito
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: 'María García',
                role: 'Dueña de Restaurant',
                quote: 'Desde que usamos QR Mesa, los pedidos llegan directos a cocina. Mis mozos tienen más tiempo para atender bien.',
                rating: 5
              },
              {
                name: 'Carlos Rodríguez',
                role: 'Gerente de Parador',
                quote: 'En verano atendemos 200 sombrillas. Sin este sistema sería imposible. Los clientes piden desde la reposera.',
                rating: 5
              },
              {
                name: 'Laura Mendez',
                role: 'Chef Ejecutiva',
                quote: 'Ver los pedidos en pantalla cambió todo. Ya no hay papelitos perdidos ni confusiones con los platos.',
                rating: 5
              }
            ].map((testimonial, i) => (
              <Card key={i} className="bg-white/5 border-white/10">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, j) => (
                      <Star key={j} className="w-5 h-5 text-yellow-500 fill-current" />
                    ))}
                  </div>
                  <p className="text-white/70 mb-6 leading-relaxed">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-white/50 text-sm">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-violet-600 to-purple-600 p-8 md:p-16 text-center">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.2) 1px, transparent 1px)',
                backgroundSize: '24px 24px'
              }} />
            </div>
            <div className="relative">
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                ¿Listo para facturar más?
              </h2>
              <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
                Empezá hoy mismo. Configurá tu negocio en minutos y 
                tus clientes ya pueden pedir desde el celular.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="bg-white text-purple-600 hover:bg-white/90 text-lg px-10 h-14 font-semibold w-full sm:w-auto">
                    Crear cuenta gratis
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <QrCode className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl">QR Mesa</span>
            </div>
            
            <div className="flex items-center gap-6 text-white/50 text-sm">
              <a href="#" className="hover:text-white transition-colors">Términos</a>
              <a href="#" className="hover:text-white transition-colors">Privacidad</a>
              <a href="#" className="hover:text-white transition-colors">Contacto</a>
            </div>

            <p className="text-white/40 text-sm">
              © 2024 QR Mesa. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
